import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Download, ArrowRight, Mic, ShoppingBag, Shirt, Dumbbell, Crown, Sun, Wind, Moon, Flame, Shield, User, ChevronDown, Smartphone, Pen, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ContactFormModal from "@/components/ContactFormModal";
import { useCart } from "@/components/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CountdownTimer from "@/components/CountdownTimer";

import { API } from "@/lib/api";

const IMAGES = {
  bookCover: "/images/ebook-cover.jpg",
  authorPhoto: "/images/merls-headshot.jpg",
  speakingBg: "https://images.unsplash.com/photo-1721623777765-1381ba32859c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtYW4lMjBzcGVha2luZyUyMG9uJTIwc3RhZ2UlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzU3MDI2OTV8MA&ixlib=rb-4.1.0&q=85",
  abstractGold: "https://images.unsplash.com/photo-1647101734210-ff5d79813069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ29sZCUyMGFic3RyYWN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3NzU3MDI3MDZ8MA&ixlib=rb-4.1.0&q=85",
};

const CLARITY_ITEMS = [
  { letter: "C", title: "Clean Food & Nutrition", desc: "Fuel your body with whole, unprocessed food. Remove the poison.", icon: Crown },
  { letter: "L", title: "Light & Sunshine", desc: "Get outside. Natural light resets your circadian rhythm and your mood.", icon: Sun },
  { letter: "A", title: "Air & Breathwork", desc: "Conscious breathing calms the nervous system. Simple. Powerful.", icon: Wind },
  { letter: "R", title: "Rest & Resistance Training", desc: "Sleep like it matters. Lift like it counts. Your body needs both.", icon: Moon },
  { letter: "I", title: "Inflammation & Insulin", desc: "Reduce chronic inflammation. Stabilise blood sugar. Feel the difference.", icon: Flame },
  { letter: "T", title: "Temperance & Trust", desc: "Moderation in all things. Trust the process. Trust yourself.", icon: Shield },
  { letter: "Y", title: "You \u2014 It's All On You", desc: "Nobody is coming to save you. Take ownership. That's where freedom lives.", icon: User },
];

// ===== HERO SECTION =====
function HeroSection({ scrollToSection }) {
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.abstractGold} alt="Find Your Breakthrough FYB brand texture - Chris Merlo The Forty Year Bender" className="w-full h-full object-cover opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="space-y-8">
            <div className="space-y-2 animate-fade-in">
              <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Out Now</p>
            </div>

            <div className="space-y-4 opacity-0 animate-fade-in-up stagger-2">
              <h1 className="font-brand text-5xl sm:text-7xl lg:text-8xl tracking-tighter text-white leading-[0.9] uppercase">
                The Forty<br />Year Bender
              </h1>
              <p className="text-base sm:text-lg text-[#D4AF37] font-heading tracking-wide">
                When Life Goes Off Track, There's a Way Back
              </p>
            </div>

            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-lg opacity-0 animate-fade-in-up stagger-3">
              Seven rehabs. Two overdoses. Forty years of chaos. One man's raw, unfiltered journey from rock bottom to redemption &mdash; and the protocol that changed everything.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up stagger-4">
              <button
                onClick={() => scrollToSection("pricing")}
                className="btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm animate-pulse-gold"
                data-testid="hero-preorder-button"
              >
                Purchase A Book Now
              </button>
              <button
                onClick={() => scrollToSection("lead-magnets")}
                className="border border-white/20 text-white px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300"
                data-testid="hero-free-chapter-button"
              >
                Get Free Chapter
              </button>
            </div>

            <p className="text-zinc-500 text-sm opacity-0 animate-fade-in-up stagger-5">
              <span className="text-[#D4AF37] font-semibold">$29.99 AUD + shipping</span>
            </p>
          </div>

          {/* Right: Book Cover */}
          <div className="flex justify-center lg:justify-end opacity-0 animate-fade-in-up stagger-3">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#D4AF37]/10 blur-3xl rounded-full" />
              <img
                src={IMAGES.bookCover}
                alt="The Forty Year Bender by Chris Merlo - Find Your Breakthrough FYB - Book Cover - When Life Goes Off Track There's a Way Back"
                title="The Forty Year Bender by Chris Merlo - Pre-Order Now"
                className="relative w-72 sm:w-80 lg:w-96 shadow-2xl shadow-black/50 img-zoom"
                data-testid="book-cover-image"
              />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-[#D4AF37] w-6 h-6" />
        </div>
      </div>
    </section>
  );
}

// ===== SOCIAL PROOF BAR =====
function SocialProofBar({ stats }) {
  const proofs = [
    { value: "7", label: "Rehabs Survived" },
    { value: "40+", label: "Years of Chaos" },
    { value: "1", label: "Protocol" },
    { value: `${stats.community_members || 347}+`, label: "In The Community" },
  ];

  return (
    <section data-testid="social-proof-bar" className="bg-[#121212] border-y border-white/10 py-8">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {proofs.map((p) => (
          <div key={p.label} className="text-center">
            <div className="font-heading text-3xl sm:text-4xl text-[#D4AF37]">{p.value}</div>
            <div className="text-xs tracking-[0.15em] uppercase text-zinc-500 mt-1">{p.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ===== AUTHOR STORY SECTION =====
function AuthorSection() {
  return (
    <section id="story" data-testid="author-section" className="py-24 sm:py-32 px-6 relative noise-overlay">
      <div className="relative z-10 max-w-7xl mx-auto">
        <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Meet Chris "Merls" Merlo</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Story */}
          <div className="space-y-6">
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white">
              "I Am Living Proof"
            </h2>

            <blockquote className="border-l-2 border-[#D4AF37] pl-6 text-zinc-300 italic text-base sm:text-lg leading-relaxed">
              "I am living proof a man can spend four decades wrecking himself and still crawl back into daylight.
              If that isn't hope, it's at least better odds than I ever had."
            </blockquote>

            <div className="gold-line-left w-24" />

            <p className="text-zinc-400 leading-relaxed">
              Don't drink. He'd drink like a drought. Don't take drugs. He'd run a pharmacy.
              Get healthy. He'd stay awake until dawn, smoke like wreckage, double the cocaine,
              and treat water as folklore.
            </p>

            <p className="text-zinc-400 leading-relaxed">
              Seven rehabs. Two accidental overdoses. Arrests here and overseas. Car wrecks,
              courtrooms, lost businesses, a broken marriage, financial ruin, and years of
              creative self-annihilation. There's nothing noble about that list.
            </p>

            <p className="text-white font-semibold text-lg">
              But here's why this book exists: If he survived it, you can survive your version.
            </p>

            <p className="text-zinc-400 leading-relaxed">
              He rebuilt himself. Changed careers. Developed discipline. Incorporated movement.
              Ate food that wasn't poison. And most shocking of all &mdash; he finally had clarity of mind.
              Not a resurrection in fire. Just a man digging himself out of a hole he'd spent
              years tunnelling. Still bruised. Still ridiculous. But finally standing.
            </p>
          </div>

          {/* Right: Author Photo */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative group">
              <div className="absolute -inset-1 border border-[#D4AF37]/30 rounded-sm translate-x-3 translate-y-3" />
              <div className="relative overflow-hidden rounded-sm border border-white/10 max-w-md">
                <img
                  src={IMAGES.authorPhoto}
                  alt="Chris Merlo - Author of The Forty Year Bender - Find Your Breakthrough FYB - Speaker and Creator of the CLARITY Protocol"
                  title="Chris Merlo - Author, Speaker, Creator of the CLARITY Protocol"
                  className="relative w-full object-cover object-top"
                  style={{ aspectRatio: '3/4' }}
                  data-testid="author-photo"
                />
                {/* Gradient overlay to blend gray bg into dark theme */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#050505]/30" />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#050505]/30" />
              </div>
              <div className="absolute bottom-4 left-4 z-10 bg-[#050505]/90 backdrop-blur-sm px-4 py-2 border border-[#D4AF37]/30 rounded-sm">
                <p className="font-brand text-lg text-white">Chris "Merls" Merlo</p>
                <p className="text-xs text-[#D4AF37] tracking-wider uppercase">Author &bull; Speaker &bull; Survivor</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== CLARITY PROTOCOL SECTION =====
function ProtocolSection() {
  return (
    <section id="protocol" data-testid="protocol-section" className="py-24 sm:py-32 px-6 bg-[#0a0a0a] relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">The Clarity Protocol</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
            Reset Your Nervous System in 30 Days
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Born from forty years of trial and error. A simple, powerful system that doesn't require
            a guru, a retreat, or a miracle. Just seven pillars and your commitment.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {CLARITY_ITEMS.map((item, i) => {
            const Icon = item.icon;
            const isLarge = i === 0 || i === 6;
            return (
              <div
                key={item.letter}
                className={`border-gold-glow card-hover bg-[#121212] p-6 sm:p-8 rounded-sm ${isLarge ? "sm:col-span-2 lg:col-span-1" : ""}`}
                data-testid={`protocol-item-${item.letter}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                    <span className="font-brand text-2xl text-[#D4AF37]">{item.letter}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-[#D4AF37]" />
                      <h3 className="font-heading text-xl tracking-tight uppercase text-white">{item.title}</h3>
                    </div>
                    <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <a
            href="/clarity-protocol"
            className="inline-flex items-center gap-2 text-[#D4AF37] text-sm tracking-[0.15em] uppercase font-semibold hover:text-[#E5C07B] transition-colors"
            data-testid="protocol-learn-more"
          >
            Dive Deeper Into The Protocol <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

// ===== SCIENCE SECTION =====
function ScienceSection() {
  const articles = [
    {
      slug: "sleep-light-3am",
      title: "Why You Wake at 3am: Sleep, Light and a Nervous System That Won't Switch Off",
      excerpt: "Two of the biggest fixes cost nothing: light and sleep. Here's why they matter more than almost anything else.",
      date: "September 26, 2026",
      readTime: "7 min",
    },
    {
      slug: "the-bourbon-lie",
      title: "The Bourbon Lie: What Alcohol Really Does to a Stressed Nervous System",
      excerpt: "For most of forty years I told myself a drink took the edge off. Here is the biology of that trade.",
      date: "August 26, 2026",
      readTime: "8 min",
    },
    {
      slug: "starting-feels-impossible",
      title: "\"It's Too Much\": Why Starting Feels Impossible — and the Science That Says It Isn't",
      excerpt: "If some part of you has already decided it's too much — I want to talk to that part directly.",
      date: "July 26, 2026",
      readTime: "10 min",
    },
    {
      slug: "what-heals",
      title: "What Actually Heals a Nervous System? Sorting the Science from the Noise",
      excerpt: "Out of the thousand methods online, which ones actually work? Here's what the research backs.",
      date: "June 26, 2026",
      readTime: "14 min",
    },
    {
      slug: "nervous-system",
      title: "What Is Your Nervous System — and How Do You Know It's Wrecked?",
      excerpt: "For forty years I spiked my nervous system. It turns out I had the machinery completely wrong.",
      date: "April 24, 2026",
      readTime: "12 min",
    },
  ];

  return (
    <section id="science" data-testid="science-section" className="py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-3">Research-Backed</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white mb-3">
            The Science Behind CLARITY
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm">
            Merls isn't a scientist — so he checked every claim against the researchers. Read the evidence behind the protocol.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {articles.map((a) => (
            <a
              key={a.slug}
              href={`/science/${a.slug}`}
              className="block border-gold-glow card-hover bg-[#121212] p-6 rounded-sm group"
              data-testid={`science-card-${a.slug}`}
            >
              <div className="flex items-center gap-3 text-xs text-zinc-500 mb-3">
                <span>{a.date}</span>
                <span>&bull;</span>
                <span>{a.readTime} read</span>
              </div>
              <h3 className="font-heading text-xl tracking-tight uppercase text-white group-hover:text-[#D4AF37] transition-colors mb-2">
                {a.title}
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed mb-4">{a.excerpt}</p>
              <span className="text-[#D4AF37] text-xs tracking-[0.15em] uppercase font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                Read Article <ArrowRight className="w-3 h-3" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== CLARITY PROTOCOL WAITLIST =====
function ProtocolWaitlistSection() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, form);
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="protocol-waitlist" data-testid="protocol-waitlist-section" className="py-24 sm:py-32 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Coming Soon</p>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
          The Clarity Protocol Program
        </h2>
        <p className="text-zinc-400 max-w-xl mx-auto text-base leading-relaxed mb-12">
          The full 30-day guided program is launching soon. Join the waitlist now to lock in
          exclusive founding-member pricing and bonus features before anyone else.
        </p>

        {!submitted ? (
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 sm:p-10 rounded-sm max-w-md mx-auto space-y-4" data-testid="waitlist-form">
            <h3 className="font-heading text-xl tracking-tight uppercase text-white mb-2">Join The Waitlist</h3>
            <p className="text-zinc-500 text-xs mb-4">Be first in line. Founding members get special pricing &amp; extra features.</p>
            <Input
              type="text"
              placeholder="First name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="waitlist-firstname-input"
            />
            <Input
              type="text"
              placeholder="Last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="waitlist-lastname-input"
            />
            <Input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="waitlist-email-input"
            />
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
              data-testid="waitlist-submit-button"
            >
              {loading ? "Submitting..." : "Join The Waitlist"}
            </Button>
          </div>
        ) : (
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 sm:p-10 rounded-sm max-w-md mx-auto text-center space-y-4" data-testid="waitlist-success">
            <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center mx-auto">
              <ArrowRight className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Thank You</h3>
            <p className="text-zinc-400 text-sm">
              You're on the list. We'll be in touch with founding-member details and exclusive pricing before anyone else gets access.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ===== LEAD MAGNETS SECTION =====
function LeadMagnetsSection() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loadingType, setLoadingType] = useState(null);

  const handleLeadSubmit = async (type) => {
    if (!email) { toast.error("Please enter your email"); return; }
    setLoadingType(type);
    try {
      if (type === "both") {
        await Promise.all([
          axios.post(`${API}/leads`, { email, lead_type: "chapter" }),
          axios.post(`${API}/leads`, { email, lead_type: "guide" }),
        ]);
        navigate("/thank-you?type=both");
      } else {
        await axios.post(`${API}/leads`, { email, lead_type: type });
        navigate(`/thank-you?type=${type}`);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <section id="lead-magnets" data-testid="lead-magnets-section" className="py-24 sm:py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Free Resources</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
            Start Your Breakthrough Today
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Not ready to commit? Start with a free taste. No strings. No spam. Just value.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Two cards describing the resources */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Free Chapter */}
            <div className="border-gold-glow bg-[#121212] p-8 rounded-sm space-y-4">
              <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Free Chapter</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Read the prologue of The Forty Year Bender. Raw. Unfiltered. Unforgettable.
                Decide for yourself if this is the book you need right now.
              </p>
            </div>

            {/* Clarity Mini-Guide */}
            <div className="border-gold-glow bg-[#121212] p-8 rounded-sm space-y-4">
              <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                <Download className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Clarity Mini-Guide</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                The full 7-step protocol to reset your nervous system in 30 days.
                Detailed action steps. Science-backed. Zero fluff.
              </p>
            </div>
          </div>

          {/* Single email form with choice buttons */}
          <div className="bg-[#121212] border border-white/10 p-8 rounded-sm max-w-xl mx-auto space-y-5">
            <p className="text-center text-zinc-300 text-sm font-medium">Enter your email and choose your free resource:</p>
            <Input
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="lead-email-input"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={() => handleLeadSubmit("chapter")}
                disabled={!!loadingType}
                className="h-12 rounded-sm text-xs tracking-[0.1em] uppercase border border-white/20 bg-transparent text-white hover:bg-white/5 hover:border-[#D4AF37]"
                data-testid="chapter-submit-button"
              >
                {loadingType === "chapter" ? "Sending..." : "Get Free Chapter"}
              </Button>
              <Button
                onClick={() => handleLeadSubmit("guide")}
                disabled={!!loadingType}
                className="h-12 rounded-sm text-xs tracking-[0.1em] uppercase border border-white/20 bg-transparent text-white hover:bg-white/5 hover:border-[#D4AF37]"
                data-testid="guide-submit-button"
              >
                {loadingType === "guide" ? "Sending..." : "Get Mini-Guide"}
              </Button>
            </div>
            <Button
              onClick={() => handleLeadSubmit("both")}
              disabled={!!loadingType}
              className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
              data-testid="both-submit-button"
            >
              {loadingType === "both" ? "Sending..." : "Get Both Free Resources"}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== AUDIOBOOK CARD (with countdown until available) =====
const AUDIOBOOK_LAUNCH = new Date("2026-06-04T08:00:00Z").getTime(); // 6:00 PM AEST

function AudiobookCard({ tier }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const isLive = now >= AUDIOBOOK_LAUNCH;
  const diff = Math.max(0, AUDIOBOOK_LAUNCH - now);
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return (
    <>
      {tier.artwork && (
        <div className="flex justify-center">
          <img
            src={tier.artwork}
            alt="The Forty Year Bender Audiobook - Narrated by Chris Merlo"
            className="w-full max-w-[200px] rounded-sm shadow-lg shadow-black/50"
          />
        </div>
      )}
      {tier.audiobookPrice && (
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-5xl text-[#D4AF37]">${tier.audiobookPrice.toFixed(2)}</span>
            <span className="text-xs text-zinc-500 font-medium">AUD</span>
          </div>
        </div>
      )}
      <Separator className="bg-white/10" />
      <ul className="space-y-3 text-sm text-zinc-300">
        {tier.features.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> {f}
          </li>
        ))}
      </ul>
      {isLive ? (
        <>
          <a
            href={tier.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase flex items-center justify-center gap-2"
            data-testid="audiobook-buy-button"
          >
            Listen Now <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-zinc-600 text-[10px] text-center">Audiobook is hosted on Awesound and processed separately to physical and eBook purchases.</p>
        </>
      ) : (
        <div className="space-y-3">
          <div className="bg-[#050505]/50 border border-[#D4AF37]/30 px-4 py-3 rounded-sm text-center">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37] font-bold mb-2">Available In</p>
            <div className="flex justify-center gap-2">
              <div className="text-center">
                <span className="font-heading text-2xl text-white">{String(hours).padStart(2, "0")}</span>
                <p className="text-[8px] text-zinc-500 uppercase tracking-wider">Hrs</p>
              </div>
              <span className="font-heading text-2xl text-[#D4AF37]">:</span>
              <div className="text-center">
                <span className="font-heading text-2xl text-white">{String(minutes).padStart(2, "0")}</span>
                <p className="text-[8px] text-zinc-500 uppercase tracking-wider">Min</p>
              </div>
              <span className="font-heading text-2xl text-[#D4AF37]">:</span>
              <div className="text-center">
                <span className="font-heading text-2xl text-white">{String(seconds).padStart(2, "0")}</span>
                <p className="text-[8px] text-zinc-500 uppercase tracking-wider">Sec</p>
              </div>
            </div>
          </div>
          <div className="w-full h-12 rounded-sm text-xs tracking-[0.15em] uppercase border border-white/10 bg-transparent text-zinc-500 flex items-center justify-center">
            Drops 4 June &mdash; 6:00 PM AEST
          </div>
        </div>
      )}
    </>
  );
}

// ===== PRICING SECTION =====
function PricingSection() {
  const { addItem } = useCart();
  const [quantities, setQuantities] = useState({ early_bird: 1 });

  const updateQty = (id, delta) => {
    setQuantities((prev) => ({ ...prev, [id]: Math.max(1, Math.min(20, (prev[id] || 1) + delta)) }));
  };

  const handleAddToCart = (tier) => {
    const qty = quantities[tier.id] || 1;
    addItem(tier.id, tier.name, tier.price, qty);
    toast.success(`${tier.name}${qty > 1 ? ` x${qty}` : ""} added to cart`);
  };

  const tiers = [
    {
      id: "digital",
      name: "eBook",
      price: 14.99,
      currency: "AUD",
      desc: "Digital download \u2014 Read anywhere",
      features: ["Instant digital delivery via email", "EPUB format", "Read on any device", "No shipping required"],
      highlight: false,
      hasQty: false,
    },
    {
      id: "early_bird",
      name: "Physical Copy",
      price: 29.99,
      currency: "AUD",
      desc: "First edition paperback",
      features: ["First edition paperback", "Ships from Australia", "Available now"],
      highlight: true,
      badge: "Available Now",
      shippingNote: "+ shipping at checkout",
      hasQty: true,
    },
    {
      id: "audiobook",
      name: "Audiobook",
      price: null,
      currency: "AUD",
      desc: "Narrated by Chris Merlo",
      artwork: "/images/audiobook-cover.png",
      link: "https://awesound.com/a/the-40-year-bender",
      features: ["Narrated by the author himself", "Listen anywhere, anytime", "Available on Awesound"],
      highlight: false,
      badge: "New",
      hasQty: false,
      isAudiobook: true,
      audiobookPrice: 14.99,
    },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 px-6 bg-[#0a0a0a] relative">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Get Your Copy</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
            Purchase A Book Now
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto">
            The Forty Year Bender is available now. Grab the paperback or listen to the audiobook narrated by Merls himself.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const qty = quantities[tier.id] || 1;
            return (
              <div
                key={tier.id}
                className={`relative border rounded-sm p-8 space-y-6 transition-all duration-300 hover:translate-y-[-4px] ${
                  tier.highlight
                    ? "border-[#D4AF37] bg-[#121212] shadow-lg shadow-[#D4AF37]/10"
                    : "border-white/10 bg-[#121212] hover:border-[#D4AF37]/50"
                }`}
                data-testid={`pricing-card-${tier.id}`}
              >
                {tier.badge && (
                  <div className={`absolute -top-3 left-6 px-3 py-1 text-[10px] font-bold tracking-[0.15em] uppercase rounded-sm ${
                    tier.highlight ? "bg-[#D4AF37] text-[#050505]" : "bg-white/10 text-[#D4AF37] border border-[#D4AF37]/30"
                  }`}>
                    {tier.badge}
                  </div>
                )}
                <div>
                  <h3 className="font-heading text-2xl tracking-tight uppercase text-white">{tier.name}</h3>
                  <p className="text-xs text-zinc-500 mt-1">{tier.desc}</p>
                </div>
                {tier.isAudiobook ? (
                  <AudiobookCard tier={tier} />
                ) : (
                <>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-5xl text-[#D4AF37]">${tier.price.toFixed(2)}</span>
                    <span className="text-xs text-zinc-500 font-medium">{tier.currency}{tier.hasQty ? " each" : ""}</span>
                  </div>
                  {tier.shippingNote && <p className="text-[10px] text-zinc-600 mt-1">{tier.shippingNote}</p>}
                </div>

                {/* Quantity selector for physical books */}
                {tier.hasQty && (
                  <div className="flex items-center justify-between bg-[#050505]/50 border border-white/10 px-4 py-2 rounded-sm">
                    <span className="text-xs text-zinc-400 uppercase tracking-wider">Qty</span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateQty(tier.id, -1)} className="w-8 h-8 border border-white/20 rounded-sm text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center font-heading text-lg" data-testid={`qty-minus-${tier.id}`}>-</button>
                      <span className="font-heading text-xl text-white w-6 text-center">{qty}</span>
                      <button onClick={() => updateQty(tier.id, 1)} className="w-8 h-8 border border-white/20 rounded-sm text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center font-heading text-lg" data-testid={`qty-plus-${tier.id}`}>+</button>
                    </div>
                    {qty > 1 && <span className="text-xs text-[#D4AF37] font-semibold">${(tier.price * qty).toFixed(2)}</span>}
                  </div>
                )}

                <Separator className="bg-white/10" />
                <ul className="space-y-3 text-sm text-zinc-300">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleAddToCart(tier)}
                  className={`w-full h-12 rounded-sm text-xs tracking-[0.15em] uppercase ${
                    tier.highlight
                      ? "btn-gold animate-pulse-gold"
                      : "border border-white/20 bg-transparent text-white hover:bg-white/5 hover:border-[#D4AF37]"
                  }`}
                  data-testid={`checkout-${tier.id}-button`}
                >
                  {tier.highlight
                    ? (qty > 1 ? `Add ${qty} Copies To Cart` : "Add To Cart")
                    : (qty > 1 ? `Add ${qty} To Cart` : "Add To Cart")}
                </Button>
                </>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-center text-zinc-600 text-xs mt-8">
          All prices in AUD. Secure checkout powered by Stripe.
        </p>
      </div>
    </section>
  );
}

// ===== LAUNCH TICKETS SECTION =====
function LaunchTicketsSection() {
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "" });
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);

  const handleWaitlistSubmit = async () => {
    if (!waitlistForm.name || !waitlistForm.email || !waitlistForm.phone) {
      toast.error("Please fill in all fields");
      return;
    }
    setWaitlistLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: waitlistForm.name,
        email: waitlistForm.email,
        message: `CANCELLATION WAITLIST - Phone: ${waitlistForm.phone} - Requesting ticket if cancellation becomes available for the Official Book Launch on 4 June 2026.`,
      });
      setWaitlistSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <section id="launch-tickets" data-testid="launch-tickets-section" className="py-24 sm:py-32 px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0808] to-[#050505]" />
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* SOLD OUT Banner */}
        <div className="bg-red-600 text-white text-center py-5 px-6 rounded-sm mb-12" data-testid="sold-out-banner">
          <p className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">Sold Out</p>
          <p className="text-sm sm:text-base mt-2 opacity-90">All 388 tickets for the official book launch have been sold</p>
        </div>

        <div className="text-center mb-12">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Official Book Launch</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white mb-4">
            The Forty Year Bender &mdash; Live at La Boite
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base leading-relaxed">
            The response has been incredible. 388 seats &mdash; gone. Merls is blown away by the support.
            If you missed out, join the cancellation list below and we'll be in touch if a spot opens up.
          </p>
        </div>

        <div className="border border-white/10 bg-[#121212] p-8 sm:p-12 rounded-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* Event Details */}
            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                    <span className="font-heading text-lg text-[#D4AF37]">04</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Thursday, 4 June 2026</p>
                    <p className="text-zinc-500 text-xs">Doors open 6:30 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                    <Mic className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">La Boite Theatre</p>
                    <p className="text-zinc-500 text-xs">Kelvin Grove, Brisbane</p>
                  </div>
                </div>
              </div>
              <div className="gold-line-left w-16" />
              <div className="bg-[#050505]/50 border border-white/10 px-4 py-3 rounded-sm">
                <div className="w-full bg-white/10 h-1.5 rounded-full">
                  <div className="bg-red-600 h-1.5 rounded-full w-full" />
                </div>
                <p className="text-[10px] text-red-400 mt-2 font-semibold text-center">388 / 388 TICKETS SOLD</p>
              </div>
            </div>

            {/* Cancellation Waitlist Form */}
            <div>
              {!waitlistSubmitted ? (
                <div className="space-y-4" data-testid="waitlist-form">
                  <h3 className="font-heading text-xl tracking-tight uppercase text-white">Cancellation List</h3>
                  <p className="text-zinc-400 text-sm">Missed out? Leave your details and we'll contact you if a ticket becomes available.</p>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={waitlistForm.name}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                    data-testid="waitlist-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={waitlistForm.email}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                    data-testid="waitlist-email-input"
                  />
                  <Input
                    type="tel"
                    placeholder="Your phone number"
                    value={waitlistForm.phone}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, phone: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                    data-testid="waitlist-phone-input"
                  />
                  <Button
                    onClick={handleWaitlistSubmit}
                    disabled={waitlistLoading}
                    className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                    data-testid="waitlist-submit-button"
                  >
                    {waitlistLoading ? "Submitting..." : "Join Cancellation List"}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 space-y-4" data-testid="waitlist-success">
                  <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center mx-auto">
                    <span className="font-heading text-2xl text-[#D4AF37]">!</span>
                  </div>
                  <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Thank You</h3>
                  <p className="text-zinc-400 text-sm">Your details have been submitted. We'll be in touch soon if a spot opens up.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== BOOK LAUNCH EVENT SECTION =====
function LaunchEventSection() {
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [ticketQty, setTicketQty] = useState(1);
  const [showContact, setShowContact] = useState(false);

  const handleEventCheckout = async () => {
    setLoadingEvent(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: "launch_event",
        origin_url: window.location.origin,
        email: "",
        name: "",
        quantity: ticketQty,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoadingEvent(false);
    }
  };

  return (
    <section id="launch-event" data-testid="launch-event-section" className="py-24 sm:py-32 px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0808] to-[#050505]" />
      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-600/10 border border-red-600/30 px-4 py-2 rounded-sm mb-6">
            <span className="pulse-dot" />
            <span className="text-red-400 text-xs tracking-[0.15em] uppercase font-bold">Limited First Release</span>
          </div>
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Exclusive Event</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white mb-4">
            Official Book Launch
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-base leading-relaxed">
            Be in the room when The Forty Year Bender officially launches. An intimate evening
            with Merls &mdash; the stories behind the stories, live readings, and a night
            you won't forget. First release tickets are limited &mdash; don't wait.
          </p>
        </div>

        <div className="border-2 border-[#D4AF37] bg-[#121212] p-8 sm:p-12 rounded-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {/* Event Details */}
            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                    <span className="font-heading text-lg text-[#D4AF37]">04</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Thursday, 4 June 2026</p>
                    <p className="text-zinc-500 text-xs">Doors open 6:30 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
                    <Mic className="w-4 h-4 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">La Boite Theatre</p>
                    <p className="text-zinc-500 text-xs">Kelvin Grove, Brisbane</p>
                  </div>
                </div>
              </div>
              <div className="gold-line-left w-16" />
              <h3 className="font-heading text-lg tracking-tight uppercase text-white">Each Ticket Includes:</h3>
              <ul className="space-y-2 text-sm text-zinc-300">
                <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> Allocated seat at the official launch</li>
                <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> Signed copy of The Forty Year Bender</li>
                <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> Exclusive FYB ball cap</li>
                <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> Live reading &amp; Q&A with Merls</li>
                <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37] flex-shrink-0" /> Meet the author</li>
              </ul>
            </div>

            {/* Price & CTA */}
            <div className="text-center space-y-6">
              <div>
                <div className="font-heading text-6xl text-[#D4AF37]">$77</div>
                <p className="text-xs text-zinc-500 mt-1">AUD per ticket &bull; Tax inclusive &bull; No booking fees</p>
              </div>

              {/* Quantity Selector */}
              <div className="flex items-center justify-center gap-4" data-testid="ticket-quantity-selector">
                <button
                  onClick={() => setTicketQty(Math.max(1, ticketQty - 1))}
                  className="w-10 h-10 border border-white/20 rounded-sm text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center font-heading text-xl"
                  data-testid="ticket-qty-minus"
                >
                  -
                </button>
                <div className="text-center">
                  <span className="font-heading text-3xl text-white">{ticketQty}</span>
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{ticketQty === 1 ? "ticket" : "tickets"}</p>
                </div>
                <button
                  onClick={() => setTicketQty(Math.min(25, ticketQty + 1))}
                  className="w-10 h-10 border border-white/20 rounded-sm text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center font-heading text-xl"
                  data-testid="ticket-qty-plus"
                >
                  +
                </button>
              </div>

              {ticketQty > 1 && (
                <p className="text-sm text-[#D4AF37] font-semibold">
                  Total: ${(77 * ticketQty).toFixed(2)} AUD
                </p>
              )}

              <Button
                onClick={handleEventCheckout}
                disabled={loadingEvent}
                className="w-full btn-gold h-14 rounded-sm text-sm tracking-[0.15em] uppercase animate-pulse-gold"
                data-testid="checkout-launch-event-button"
              >
                {loadingEvent ? "Redirecting to checkout..." : `Get ${ticketQty > 1 ? ticketQty + " Tickets" : "Your Ticket"}`}
              </Button>
              <p className="text-zinc-600 text-[10px]">Secure checkout powered by Stripe. Tickets are non-refundable.</p>
              <p className="text-zinc-500 text-[10px]">Attending with a group? Purchase all seats in one transaction to be seated together.</p>
              <p className="text-zinc-600 text-[10px]">Need more than 25 tickets? <button onClick={() => setShowContact(true)} className="text-[#D4AF37] hover:underline">Get in touch</button></p>
            </div>
          </div>
        </div>
      </div>
      <ContactFormModal open={showContact} onClose={() => setShowContact(false)} />
    </section>
  );
}

// ===== MEDIA & PRESS SECTION =====
function MediaSection() {
  const [mediaForm, setMediaForm] = useState({ name: "", email: "", outlet: "", message: "" });
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaSubmitted, setMediaSubmitted] = useState(false);

  const mediaItems = [
    {
      outlet: "ABC Radio Brisbane",
      title: "Drive with Ellen Fanning",
      desc: "Merls joined acclaimed journalist Ellen Fanning on ABC Radio Brisbane Drive to talk about addiction, recovery, and the story behind The Forty Year Bender.",
      date: "May 7, 2026",
      type: "Radio",
    },
    {
      outlet: "The Village Voice",
      title: "The Redemption of Chris Merlo",
      desc: "An in-depth feature exploring the raw, unfiltered journey from forty years of chaos to the man standing today — and why this story matters.",
      date: "June 2026",
      type: "Feature",
    },
    {
      outlet: "Sunday Mail / Courier Mail",
      title: "From Bender to a Memoir",
      desc: "Mike Gambaro's piece on how a lifetime of addiction, seven rehabs, and two overdoses became one of the most anticipated Australian memoirs of the year.",
      date: "June 2026",
      type: "Print",
    },
  ];

  const handleMediaSubmit = async () => {
    if (!mediaForm.name || !mediaForm.email || !mediaForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setMediaLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: mediaForm.name,
        email: mediaForm.email,
        message: `MEDIA/PODCAST ENQUIRY\nOutlet: ${mediaForm.outlet}\n\n${mediaForm.message}`,
      });
      setMediaSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setMediaLoading(false);
    }
  };

  return (
    <section id="media" data-testid="media-section" className="py-24 sm:py-32 px-6 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">As Seen In</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
            In The Media
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            The Forty Year Bender and Merls' story have been featured across Australian media.
          </p>
        </div>

        {/* Media Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {mediaItems.map((item) => (
            <div
              key={item.title}
              className="border-gold-glow card-hover bg-[#121212] p-6 sm:p-8 rounded-sm space-y-4"
              data-testid={`media-card-${item.outlet.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-2 py-1 rounded-sm font-bold">{item.type}</span>
                <span className="text-[10px] text-zinc-600">{item.date}</span>
              </div>
              <div>
                <h3 className="font-heading text-xl tracking-tight uppercase text-white mb-1">{item.outlet}</h3>
                <p className="text-sm text-[#D4AF37] italic">"{item.title}"</p>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Media Kit CTA */}
        <div className="text-center my-12">
          <div className="bg-[#121212] border border-white/10 p-8 rounded-sm max-w-xl mx-auto space-y-4">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">For Press & Media</p>
            <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Download The Media Kit</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Approved images, video reels, press release, author bio, and everything you need to cover the story.
            </p>
            <a
              href="/media-kit"
              className="inline-flex items-center gap-2 btn-gold px-8 py-4 text-sm tracking-[0.15em] uppercase rounded-sm"
              data-testid="media-kit-cta"
            >
              Access Media Kit <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Media Enquiry Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 sm:p-10 rounded-sm">
            {!mediaSubmitted ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Media &amp; Podcast Enquiries</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    Interested in featuring Merls on your show, podcast, or publication?
                    We'd love to hear from you. Fill out the form and our media team will
                    be in touch within 48 hours.
                  </p>
                  <div className="gold-line-left w-16" />
                  <div className="space-y-2 text-sm text-zinc-500">
                    <p>Available for:</p>
                    <ul className="space-y-1">
                      <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37]" /> Radio &amp; TV interviews</li>
                      <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37]" /> Podcast guest appearances</li>
                      <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37]" /> Print &amp; online features</li>
                      <li className="flex items-center gap-2"><ArrowRight className="w-3 h-3 text-[#D4AF37]" /> Keynote speaking engagements</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-4">
                  <Input
                    type="text"
                    placeholder="Your name *"
                    value={mediaForm.name}
                    onChange={(e) => setMediaForm({ ...mediaForm, name: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                    data-testid="media-name-input"
                  />
                  <Input
                    type="email"
                    placeholder="Your email *"
                    value={mediaForm.email}
                    onChange={(e) => setMediaForm({ ...mediaForm, email: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                    data-testid="media-email-input"
                  />
                  <Input
                    type="text"
                    placeholder="Publication / Show / Podcast name"
                    value={mediaForm.outlet}
                    onChange={(e) => setMediaForm({ ...mediaForm, outlet: e.target.value })}
                    className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                    data-testid="media-outlet-input"
                  />
                  <textarea
                    placeholder="Tell us about the opportunity... *"
                    value={mediaForm.message}
                    onChange={(e) => setMediaForm({ ...mediaForm, message: e.target.value })}
                    rows={3}
                    className="w-full bg-black border border-white/20 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none text-white placeholder:text-zinc-600 px-3 py-3 rounded-sm text-sm"
                    data-testid="media-message-input"
                  />
                  <Button
                    onClick={handleMediaSubmit}
                    disabled={mediaLoading}
                    className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                    data-testid="media-submit-button"
                  >
                    {mediaLoading ? "Sending..." : "Submit Media Enquiry"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4" data-testid="media-success">
                <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center mx-auto">
                  <Mic className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Thank You</h3>
                <p className="text-zinc-400 text-sm">Your media enquiry has been received. Our team will be in touch within 48 hours.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== WORK WITH MERLS SECTION =====
function WorkWithMerlsSection() {
  return (
    <section id="work-with-merls" data-testid="work-with-merls-section" className="py-24 sm:py-32 px-6 relative">
      <div className="max-w-4xl mx-auto">
        <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 sm:p-12 rounded-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-5">
              <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">1:1 With Merls</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter uppercase text-white leading-tight">
                Ready To Do The Work?
              </h2>
              <p className="text-zinc-300 text-base leading-relaxed">
                No clipboard. No textbook. No waiting room. Just a man who hit rock bottom, found the road back, and for thirty days walks it beside you. Merls takes on a limited number of people for his 1:1 Recovery Protocol — direct, daily, and built on lived experience.
              </p>
              <p className="text-zinc-500 text-sm italic">
                For yourself or for someone you love.
              </p>
            </div>
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto">
                <User className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <a
                href="/recovery"
                className="block w-full btn-gold h-14 rounded-sm text-sm tracking-[0.15em] uppercase flex items-center justify-center gap-2"
                data-testid="work-with-merls-cta"
              >
                Start The Conversation <ArrowRight className="w-4 h-4" />
              </a>
              <p className="text-zinc-600 text-[10px]">Limited spots available. No pressure. Just a conversation.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== MERCH SECTION =====
function MerchSection() {
  const merchItems = [
    { name: "FYB T-Shirts", desc: "Premium black tees. Bold statement.", icon: Shirt, status: "Coming Soon" },
    { name: "Ball Caps", desc: "Clean lines. FYB branded.", icon: Crown, status: "Coming Soon" },
    { name: "Tote Bags", desc: "Carry the movement with you.", icon: ShoppingBag, status: "Coming Soon" },
    { name: "Gym Towels", desc: "Sweat with purpose.", icon: Dumbbell, status: "Coming Soon" },
  ];

  return (
    <section id="merch" data-testid="merch-section" className="py-24 sm:py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Merchandise</p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white mb-4">
            Wear The Movement
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto">
            The FYB merch line will have select items dropping alongside the book launch. Be the first to know.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {merchItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.name}
                className="border-gold-glow card-hover bg-[#121212] p-6 sm:p-8 rounded-sm text-center space-y-4"
                data-testid={`merch-item-${item.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center mx-auto">
                  <Icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-lg sm:text-xl tracking-tight uppercase text-white">{item.name}</h3>
                <p className="text-zinc-500 text-xs sm:text-sm">{item.desc}</p>
                <span className="inline-block text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-3 py-1 rounded-sm">
                  {item.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== SPEAKING SECTION =====
function SpeakingSection() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error("Please fill in your name and email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/speaking`, form);
      setSubmitted(true);
      toast.success("Inquiry sent! We'll be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="speaking" data-testid="speaking-section" className="relative py-24 sm:py-32 px-6 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.speakingBg} alt="Chris Merlo speaking on stage - Book Chris Merlo for keynotes and events - Find Your Breakthrough FYB" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Copy */}
          <div className="space-y-6">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Book Merls</p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white">
              Bring The Story To Your Stage
            </h2>
            <p className="text-zinc-300 leading-relaxed">
              Merls' raw, unfiltered speaking style has moved rooms to tears and laughter
              in the same breath. His story of addiction, rock bottom, and redemption isn't
              motivational fluff &mdash; it's a man standing in his truth, telling you exactly
              how he nearly died and how he came back.
            </p>
            <p className="text-zinc-400 leading-relaxed">
              Available for corporate events, conferences, recovery centres, schools, and
              private engagements. Every talk includes the Clarity Protocol framework.
            </p>
            <div className="flex items-center gap-3">
              <Mic className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm text-zinc-400">Keynotes &bull; Panels &bull; Workshops &bull; Private Events</span>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-[#121212]/90 backdrop-blur-sm border border-white/10 p-8 rounded-sm space-y-4">
            {submitted ? (
              <div className="text-center py-12 space-y-4" data-testid="speaking-success">
                <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto">
                  <Mic className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-2xl text-white uppercase">Inquiry Received</h3>
                <p className="text-zinc-400">We'll get back to you within 48 hours.</p>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-xl tracking-tight uppercase text-white mb-2">Send an Inquiry</h3>
                <Input
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                  data-testid="speaking-name-input"
                />
                <Input
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                  data-testid="speaking-email-input"
                />
                <Input
                  placeholder="Organization (optional)"
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                  data-testid="speaking-org-input"
                />
                <textarea
                  placeholder="Tell us about your event..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="w-full bg-black border border-white/20 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none text-white placeholder:text-zinc-600 px-3 py-3 rounded-sm text-sm"
                  data-testid="speaking-message-input"
                />
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                  data-testid="speaking-submit-button"
                >
                  {loading ? "Sending..." : "Submit Inquiry"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== EXIT INTENT MODAL =====
function ExitIntentModal({ open, onClose }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, lead_type: "chapter" });
      navigate("/thank-you?type=chapter");
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border border-[#D4AF37]/30 max-w-md" data-testid="exit-intent-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl tracking-tight uppercase text-white">Wait &mdash; Don't Leave Empty-Handed</DialogTitle>
          <DialogDescription className="text-zinc-400 mt-2">
            Grab a free chapter of The Forty Year Bender before you go.
            No strings. No spam. Just one raw, powerful chapter that might change
            how you see your own story.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <Input
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
            data-testid="exit-email-input"
          />
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
            data-testid="exit-submit-button"
          >
            {loading ? "Sending..." : "Send Me The Free Chapter"}
          </Button>
          <button onClick={onClose} className="w-full text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors" data-testid="exit-dismiss-button">
            No thanks, I'll pass on the free chapter
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== MAIN LANDING PAGE =====
export default function LandingPage() {
  const [stats, setStats] = useState({ community_members: 347, leads: 0, preorders: 0 });
  const [exitModal, setExitModal] = useState(false);
  const exitShown = useRef(false);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitShown.current) {
        exitShown.current = true;
        setExitModal(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="landing-page">
      <Navbar scrollToSection={scrollToSection} />
      <HeroSection scrollToSection={scrollToSection} />
      <SocialProofBar stats={stats} />
      <AuthorSection />
      <PricingSection />
      <ProtocolSection />
      <ScienceSection />
      <ProtocolWaitlistSection />
      <LeadMagnetsSection />
      <WorkWithMerlsSection />
      <MediaSection />
      <SpeakingSection />
      <MerchSection />
      <Footer />
      <ExitIntentModal open={exitModal} onClose={() => setExitModal(false)} />
    </div>
  );
}
